import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import NRHelpDrawerKnob from './NRHelpDrawerKnob/NRHelpDrawerKnob';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { useMatches } from 'react-router-dom';
import Markdown from 'react-markdown';
import 'github-markdown-css';

function NRHelpDrawer({
  knobDistanceFromTop = '450px',
  knobOpenLabel,
  knobCloseLabel,
  knobOpenAriaLabel = 'Open help',
  knobCloseAriaLabel = 'Close help'
}) {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const openTriggerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const matches = useMatches();

  const matchedWithHelp = [...matches].reverse().find(match => match.handle?.displayHelpDrawer && match.handle?.helpDrawerContent);

  const displayHelpDrawer = !!matchedWithHelp;
  const helpDrawerContent = matchedWithHelp?.handle?.helpDrawerContent;

  const drawerPaperSx = {
    overflow: 'visible',
    minWidth: '350px',
    maxWidth: '35vw',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    border: 'none',
    boxShadow: 'none',
    backgroundImage: 'none'
  };

  const scrollOuterSx = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    msOverflowStyle: 'none' /* IE 11 */,
    scrollbarWidth: 'none' /* Firefox 64 */,
    '&#nr-help-drawer-scroll::-webkit-scrollbar-track': {
      boxShadow: 'unset'
    },
    '&#nr-help-drawer-scroll::-webkit-scrollbar-thumb': {
      display: 'none',
      boxShadow: 'unset',
      borderRadius: 10
    },
    '&#nr-help-drawer-scroll:hover::-webkit-scrollbar-thumb': {
      display: 'unset'
    },
    '&#nr-help-drawer-scroll:hover': {
      msOverflowStyle: 'unset' /* IE 11 */,
      scrollbarWidth: 'unset' /* Firefox 64 */
    }
  };

  const asideRootSx = {
    padding: theme.spacing(3),
    boxSizing: 'border-box'
  };

  const markdownBodySx = {
    paddingBottom: theme.spacing(4),
    '&#markdown-body': {
      color: theme.palette.text.primary
    },
    '&#markdown-body a': {
      color: theme.palette.blue.link
    },
    '&#markdown-body > h1': {
      ...theme.typography.h2
    },
    '&#markdown-body > h2': {
      ...theme.typography.h3
    },
    '&#markdown-body > p': {
      ...theme.typography.body1
    }
  };

  function handleKnobClick(value) {
    setIsOpen(value);
  }

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
      requestAnimationFrame(() => openTriggerRef.current?.focus());
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const el = scrollContainerRef.current;
    if (!el) return undefined;
    const updateScrollable = () => {
      setIsScrollable(el.scrollHeight > el.clientHeight);
    };
    updateScrollable();
    const ro = new ResizeObserver(updateScrollable);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, helpDrawerContent]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const id = requestAnimationFrame(() => {
      scrollContainerRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  return (
    <>
      {displayHelpDrawer && (
        <>
          <NRHelpDrawerKnob
            ref={openTriggerRef}
            isOpen={isOpen}
            onclick={handleKnobClick}
            top={knobDistanceFromTop}
            label={knobOpenLabel || <HelpOutlineOutlinedIcon />}
            ariaLabel={knobOpenAriaLabel}
          />
          <Drawer
            anchor={'right'}
            elevation={0}
            open={isOpen}
            onClose={() => setIsOpen(false)}
            disableEscapeKeyDown
            PaperProps={{
              'aria-modal': true,
              'aria-labelledby': 'nr-help-drawer-aside',
              sx: drawerPaperSx
            }}
          >
            <NRHelpDrawerKnob
              isOpen={isOpen}
              onclick={handleKnobClick}
              top={knobDistanceFromTop}
              label={knobCloseLabel || <ChevronRightOutlinedIcon />}
              ariaLabel={knobCloseAriaLabel}
            />
            <Box ref={scrollContainerRef} id="nr-help-drawer-scroll" sx={scrollOuterSx} tabIndex={isScrollable ? 0 : -1}>
              <Box component="aside" id="nr-help-drawer-aside" role="complementary" aria-label="Help Documentation" sx={asideRootSx}>
                <Box component="div" id="markdown-body" className="markdown-body" sx={markdownBodySx}>
                  <Markdown>{helpDrawerContent}</Markdown>
                </Box>
              </Box>
            </Box>
          </Drawer>
        </>
      )}
    </>
  );
}

export default NRHelpDrawer;
